/**
 * Mobility MVP React Hooks
 * React Query hooks for mobility operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  submitMobilityQuiz, 
  uploadPelvicTiltVideo, 
  getMobilityProfile,
  type MobilityQuizAnswers,
  type MobilityProfileResponse,
  type PelvicTiltResponse
} from '@/lib/api/mobility';
import { useToast } from '@/hooks/use-toast';

/**
 * Hook to fetch user's mobility profile
 */
export function useMobilityProfile() {
  return useQuery<MobilityProfileResponse>({
    queryKey: ['mobility', 'profile'],
    queryFn: getMobilityProfile,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to submit mobility quiz
 */
export function useSubmitMobilityQuiz() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (answers: MobilityQuizAnswers) => submitMobilityQuiz(answers),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mobility', 'profile'] });
      toast({
        title: 'Mobility Profile Updated',
        description: 'Your mobility assessment has been saved.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Submission Failed',
        description: error.message || 'Failed to submit mobility quiz',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook to upload pelvic tilt video
 */
export function useUploadPelvicTilt() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ playbackId, cfStreamUid }: { playbackId: string; cfStreamUid?: string }) =>
      uploadPelvicTiltVideo(playbackId, cfStreamUid),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['mobility', 'profile'] });
      toast({
        title: 'Pelvic Tilt Assessment Complete',
        description: `Your pelvic control is classified as: ${data.pelvic_control.replace('_', ' ')}`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Upload Failed',
        description: error.message || 'Failed to upload pelvic tilt video',
        variant: 'destructive',
      });
    },
  });
}



